import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { pcmpProjectDetailQueryOptions } from '../api/pcmpProjects.api';
import { PcmpPriceGridTable } from '../components/PcmpPriceGridTable';
import { PcmpProjectDeleteConfirmation } from '../components/PcmpProjectDeleteConfirmation';
import { PcmpProjectInfoCard } from '../components/PcmpProjectInfoCard';
import { PcmpProjectStatusBadge } from '../components/PcmpProjectStatusBadge';
import { useSetPcmpProjectStatus } from '../hooks/useSetPcmpProjectStatus';

export function PcmpProjectDetailPage() {
  const { projectId } = useParams({ from: '/_auth/pcmp/projects/$projectId/' });
  const router = useRouter();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutate: setStatus, isPending: isStatusPending } = useSetPcmpProjectStatus();

  const {
    data: project,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(pcmpProjectDetailQueryOptions(projectId));

  function goToList() {
    if (router.history.canGoBack()) router.history.back();
    else void router.navigate({ to: '/pcmp/projects' });
  }

  const backButton = (
    <Button type="button" variant="outline" size="sm" onClick={goToList} className="w-fit gap-1.5">
      <ArrowLeft className="size-3.5" />
      Back
    </Button>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <PageLoader />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <ErrorState
          error={error}
          fallbackTitle="Could not load this project"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {backButton}
          <h1 className="truncate text-2xl font-semibold" title={project.displayName}>
            {project.displayName}
          </h1>
          <PcmpProjectStatusBadge isActive={project.isActive} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/pcmp/projects/$projectId/edit"
            params={{ projectId: project.id }}
            className={cn(buttonVariants({ variant: 'default' }), 'gap-1.5')}
          >
            <Pencil className="size-4" />
            Edit
          </Link>
          <Button
            type="button"
            variant="outline"
            disabled={isStatusPending}
            onClick={() =>
              setStatus({
                id: project.id,
                displayName: project.displayName,
                isActive: !project.isActive,
              })
            }
            className="gap-1.5"
          >
            {project.isActive ? <PowerOff className="size-4" /> : <Power className="size-4" />}
            {project.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsDeleteOpen(true)}
            className="gap-1.5 text-destructive hover:text-destructive"
          >
            <Trash2 className="size-4" />
            Delete
          </Button>
        </div>
      </div>

      <PcmpProjectInfoCard project={project} />

      <section className="flex flex-col gap-4 rounded-xl border bg-card p-5 shadow-sm">
        <h2 className="text-lg font-semibold">Book Prices</h2>
        <PcmpPriceGridTable project={project} />
      </section>

      <PcmpProjectDeleteConfirmation
        project={project}
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onDeleted={() => void router.navigate({ to: '/pcmp/projects' })}
      />
    </div>
  );
}
